import { yupResolver } from "@hookform/resolvers/yup";
import {
  ArrowsClockwise,
  FloppyDisk,
  GoogleLogo,
  MagnifyingGlass,
  StackPlus,
  X
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  FileInput,
  Label,
  Spinner,
  TextInput
} from "flowbite-react";
// COMMENTED OUT FOR FUTURE USE - Animation imports for Apple Ads
// import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import AnimatedModal from "../../../../components/AnimatedModal";
import Loading from "../../../../components/Loading";
import IntegrationBlockSkeleton from "../../../../components/SkeletonLoaders/IntegrationBlockSkeleton";
import { markIntegrationComplete, useAuth } from "../../../../hooks";
import { useDeleteAccount } from "../../../../hooks/delete/useDeleteAccount";
import { useIntegrations } from "../../../../hooks/get/useIntegrations";
import { useLatestSocialAccountStatus } from "../../../../hooks/get/useLatestSocialAccountStatus";
// COMMENTED OUT FOR FUTURE USE - Apple Ads hooks
// import { useActivateAppleAdsAccount } from "../../../../hooks/post/useActivateAppleAdsAccount";
// import { useAppleAdsSpending } from "../../../../hooks/post/useAppleAdsSpending";
import { useInsertAppleIAP } from "../../../../hooks/post/useInsertAppleIAP";
import { useInsertGoogleIAP } from "../../../../hooks/post/useInsertGoogleIAP";
import type { IIAPApp, IIAPPlanLimit } from "../../../../interfaces";
import {
  checkPermission,
  convertAccountsToEntities,
} from "../../../../lib/utils";
import { httpService } from "../../../../services/httpService";
import {
  API_CONFIG,
  LOCAL_STORAGE_KEYS,
  PERMISSION_MAPPER
} from "../../../../shared/constants";
import AppSelectionModal from "../components/AppSelectionModal";
import IntegrationCard from "../components/IntegrationCard";
import IntegrationStats from "../components/IntegrationStats";
// import RecentActivityLogs from "../components/RecentActivityLogs";
import {
  ADDON_ACCOUNT_STATUS,
  IAddonAccountModalState,
  IInitialConfirmationPopUpInterface,
  IInitialModalStateInterface,
  IIntegrationsSet,
  OAuthDetails,
  OAuthResponse,
} from "../interface";

const initialModalState: IInitialModalStateInterface = {
  index: -1,
  open: false,
  direction: null,
  email: "",
};

const initialConfirmationPopUpState: IInitialConfirmationPopUpInterface = {
  open: false,
  index: -1,
  direction: null,
  type: null,
};

const initialAddonAccountModalState: IAddonAccountModalState = {
  open: false,
  trackingId: "",
  platform: "google_ads",
  email: "",
  accounts: [],
  selectedAccountId: "",
};

// type AppleAdsForm = { client_id: string; key_id: string; team_id: string };

const Integrations: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [editModal, setEditModal] = useState(initialModalState);
  const [isFetchingSubAccounts, setIsFetchingSubAccounts] = useState(false);
  const [savingAddonAccount, setSavingAddonAccount] = useState(false);
  const [confirmModal, setConfirmModal] = useState(
    initialConfirmationPopUpState
  );
  // Remove unused delete modal since we're no longer deleting whole pairs
  // const [deleteModal, setDeleteModal] = useState({
  //   open: false,
  //   id: "",
  // });
  const [addonAccountModal, setAddonAccountModal] = useState(
    initialAddonAccountModalState
  );
  const [deleteAccountModal, setDeleteAccountModal] = useState({
    open: false,
    accountId: "",
    direction: null as "inward" | "outward" | null,
    trackingId: "",
  });

  // Apple IAP modal state
  const [appleIAPModal, setAppleIAPModal] = useState<{
    open: boolean;
    trackingId: string;
  }>({ open: false, trackingId: "" });

  // Google IAP modal state
  const [googleIAPModal, setGoogleIAPModal] = useState<{
    open: boolean;
    trackingId: string;
  }>({ open: false, trackingId: "" });

  // App selection modal state (non-VIP users after IAP connect)
  const [appSelectionData, setAppSelectionData] = useState<{
    platform: 'apple' | 'google';
    apps: IIAPApp[];
    planLimit: IIAPPlanLimit;
    socialAccountId: number;
  } | null>(null);

  const [isAccountUnSaved, setIsAccountUnSaved] = useState<
    string | null | undefined
  >(null);

  const [noAccountWarning, setNoAccountWarning] = useState<{
    isOpen: boolean;
    platform: 'google_ads' | 'google_admob';
    email?: string;
    warningType: 'no_platform_account' | 'no_sub_accounts' | 'missing_scope';
  }>({
    isOpen: false,
    platform: 'google_ads',
    warningType: 'no_platform_account',
  });

  // Use our custom hooks
  const { data: integrations, isLoading } = useIntegrations();
  const { data: syncStatus } = useLatestSocialAccountStatus(10000);

  const syncHealth = useMemo(() => {
    if (!syncStatus?.data) return 0;
    const statuses = Object.values(syncStatus.data);
    if (statuses.length === 0) return 0;
    const finishedCount = statuses.filter((status) => status === "finished").length;
    return Math.round((finishedCount / statuses.length) * 100);
  }, [syncStatus]);

  const lastPull = syncStatus?.latest_social_account_sync_time;

  // Merge live sync statuses from the polling API into each source so cards reflect real-time state
  const integrationsWithLiveStatus = useMemo(() => {
    if (!integrations || !syncStatus?.data) return integrations;
    const liveStatuses = syncStatus.data; // { "42": "finished", "44": "in_progress", ... }
    const applyStatus = (sources: IIntegrationsSet[]) =>
      sources.map((s) => {
        const liveStatus = s.id != null
          ? liveStatuses[String(s.id)]
          : undefined;
        return liveStatus ? { ...s, status: liveStatus as IIntegrationsSet["status"] } : s;
      });
    const result: typeof integrations = {};
    for (const [key, set] of Object.entries(integrations)) {
      result[key] = {
        ...set,
        inwards: applyStatus(set.inwards),
        outwards: applyStatus(set.outwards),
      };
    }
    return result;
  }, [integrations, syncStatus]);

  const deleteAccountMutation = useDeleteAccount();
  const insertAppleIAPMutation = useInsertAppleIAP();
  const insertGoogleIAPMutation = useInsertGoogleIAP();
  // Add this line to check if any integration exists
  const { activeTotal, activeOnlyCount, ongoingCount } = useMemo(() => {
    if (!integrations) return { activeTotal: 0, activeOnlyCount: 0, ongoingCount: 0 };
    const values = Object.values(integrations);
    const activeTotal = values.length;
    const ongoingCount = values.filter((i) => i.is_ongoing).length;
    const activeOnlyCount = activeTotal - ongoingCount;
    return { activeTotal, activeOnlyCount, ongoingCount };
  }, [integrations]);

  const hasAnyIntegration = activeTotal > 0;

  const hasIntegrationCreatePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.INTEGRATION_CREATE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasAddonDeletePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.INTEGRATION_ADDON_DELETE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  // Google IAP form values type
  type GoogleIAPForm = {
    service_account_json?: any;
    email: string;
    bucket_name: string;
  };

  // Validation schema for Google IAP
  const googleIapSchema = yup.object({
    email: yup
      .string()
      .email(t("configurations.integrations.googleIap.errors.emailInvalid"))
      .required(
        t("configurations.integrations.googleIap.errors.emailRequired")
      ),
    bucket_name: yup
      .string()
      .required(
        t("configurations.integrations.googleIap.errors.bucketRequired")
      )
      .matches(
        /^[a-z0-9_.-]{3,63}$/i,
        t("configurations.integrations.googleIap.errors.bucketInvalid")
      ),
    service_account_json: yup
      .mixed()
      .test(
        "required",
        t(
          "configurations.integrations.googleIap.errors.serviceAccountRequired"
        ),
        (value) => {
          return value instanceof FileList && value.length > 0;
        }
      )
      .test(
        "fileSize",
        t(
          "configurations.integrations.googleIap.errors.serviceAccountFileSize"
        ),
        (value) => {
          if (!(value instanceof FileList) || value.length === 0) return true;
          return value[0].size <= 2 * 1024 * 1024;
        }
      )
      .test(
        "fileType",
        t(
          "configurations.integrations.googleIap.errors.serviceAccountFileType"
        ),
        (value) => {
          if (!(value instanceof FileList) || value.length === 0) return true;
          const f = value[0];
          return (
            f.type === "application/json" ||
            (typeof f.name === "string" &&
              f.name.toLowerCase().endsWith(".json"))
          );
        }
      ),
  });

  const {
    register: registerGoogleIAP,
    handleSubmit: handleSubmitGoogleIAP,
    formState: { errors: googleIapErrors, isSubmitting: googleIapSubmitting },
    reset: resetGoogleIapForm,
  } = useForm({
    resolver: yupResolver(googleIapSchema),
    defaultValues: {
      email: "",
      bucket_name: "",
      service_account_json: undefined,
    },
  });

  const onSubmitGoogleIAP = async (values: GoogleIAPForm) => {
    if (!googleIAPModal.trackingId) return;
    const formData = new FormData();
    const file = values.service_account_json?.[0];
    if (file) {
      formData.append("service_account_json", file, file.name);
    }
    formData.append("email", values.email);
    formData.append("bucket_name", values.bucket_name);
    formData.append("tracking_id", googleIAPModal.trackingId);

    try {
      const response = await insertGoogleIAPMutation.mutateAsync({ formData });
      resetGoogleIapForm();
      setGoogleIAPModal({ open: false, trackingId: "" });

      if (response.needs_app_selection) {
        // Non-VIP: show app selection before syncing
        setAppSelectionData({
          platform: 'google',
          apps: response.apps,
          planLimit: response.plan_limit,
          socialAccountId: response.social_account_id,
        });
      } else {
        // VIP or unlimited: proceed immediately
        queryClient.invalidateQueries({ queryKey: ["integrations"] });
        markIntegrationComplete();
      }
    } catch (err) {
      console.error("Google IAP submit error:", err);
    }
  };

  // Apple IAP form values type
  type AppleIAPForm = {
    p8_path?: any;
    key_id: string;
    issuer_id: string;
    vendor_number: string;
  };

  // Validation schema for Apple IAP
  const appleSchema = yup.object({
    key_id: yup
      .string()
      .required("Key ID is required")
      .matches(/^[A-Z0-9]{10}$/i, "Key ID must be 10 alphanumeric chars"),
    issuer_id: yup
      .string()
      .required("Issuer ID is required")
      .matches(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        "Issuer ID must be a valid UUID"
      ),
    vendor_number: yup
      .string()
      .required("Vendor Number is required")
      .matches(/^\d+$/, "Vendor Number must be numeric"),
    p8_path: yup
      .mixed()
      .test("required", "P8 key file is required", (value) => {
        return value instanceof FileList && value.length > 0;
      })
      .test("fileSize", "File must be <= 2MB", (value) => {
        if (!(value instanceof FileList) || value.length === 0) return true;
        return value[0].size <= 2 * 1024 * 1024;
      })
      .test(
        "pemMarkers",
        "File must contain BEGIN/END markers",
        async (value) => {
          if (!(value instanceof FileList) || value.length === 0) return true;
          try {
            const text = await value[0].text();
            return /-----BEGIN[\s\S]*-----END/.test(text);
          } catch {
            return false;
          }
        }
      ),
  });

  const {
    register: registerApple,
    handleSubmit: handleSubmitApple,
    formState: { errors: appleErrors, isSubmitting: appleSubmitting },
    reset: resetAppleForm,
  } = useForm({
    resolver: yupResolver(appleSchema),
    defaultValues: {
      key_id: "",
      issuer_id: "",
      vendor_number: "",
      p8_path: undefined,
    },
  });

  const onSubmitAppleIAP = async (values: AppleIAPForm) => {
    if (!appleIAPModal.trackingId) return;
    const formData = new FormData();
    const file = values.p8_path?.[0];
    if (file) {
      formData.append("p8_file", file, file.name);
    }
    formData.append("key_id", values.key_id);
    formData.append("issuer_id", values.issuer_id);
    formData.append("vendor_number", values.vendor_number);
    formData.append("tracking_id", appleIAPModal.trackingId);

    try {
      const response = await insertAppleIAPMutation.mutateAsync({ formData });
      // Close and reset modal
      resetAppleForm();
      setAppleIAPModal({ open: false, trackingId: "" });

      if (response.needs_app_selection) {
        // Non-VIP: show app selection before syncing
        setAppSelectionData({
          platform: 'apple',
          apps: response.apps,
          planLimit: response.plan_limit,
          socialAccountId: response.social_account_id,
        });
      } else {
        // VIP or unlimited: proceed immediately
        queryClient.invalidateQueries({ queryKey: ["integrations"] });
      }
    } catch (err) {
      // Keep modal open and surface errors via existing toast in httpService
      console.error("Apple IAP submit error:", err);
    }
  };

  const handleSubAccountSelection = (accountId: string): void => {
    setAddonAccountModal((prev) => ({
      ...prev,
      selectedAccountId: accountId,
    }));
  };

  const handleSaveAddonAccount = async (): Promise<void> => {
    try {
      const { trackingId, platform, selectedAccountId } = addonAccountModal;
      setSavingAddonAccount(true);

      if (platform === "google_ads") {
        // Call save Google Ads account API
        await httpService.post(
          `${API_CONFIG.baseUrl}/${API_CONFIG.path.googleAdsSaveAccount}`,
          {
            merchant_customer_id: [selectedAccountId],
            tracking_id: trackingId,
          }
        );
      } else {
        // Call insert AdMob account API using httpService instead of fetch
        await httpService.get(API_CONFIG.path.insertApps, {
          publisher_ids: selectedAccountId,
          tracking_id: trackingId,
        });
      }

      setSavingAddonAccount(false);
      toast.success(t("configurations.integrations.addonAccountSaved"));
      setAddonAccountModal(initialAddonAccountModalState);
      setIsAccountUnSaved(null);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.ADD_ON);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.PLATFORM);

      // Mark integration as complete when addon account is saved
      markIntegrationComplete();

      // Invalidate the connected-accounts API to refresh the data
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    } catch (error) {
      setSavingAddonAccount(false);
      toast.error(t("configurations.integrations.addonAccountError"));
      console.error("Error saving addon account:", error);
    }
  };

  const handleConnect = async (platform: 'google_ads' | 'google_admob') => {
    const { trackingId } = addonAccountModal;
    const finalTrackingId = trackingId || localStorage.getItem(LOCAL_STORAGE_KEYS.ADD_ON);

    if (!finalTrackingId) {
      navigate("/integrations?trackingId=add");
      return;
    }

    localStorage.setItem(LOCAL_STORAGE_KEYS.ADD_ON, finalTrackingId);
    localStorage.setItem(LOCAL_STORAGE_KEYS.PLATFORM, platform);

    try {
      const details = await httpService.get<OAuthDetails>(
        `${API_CONFIG.path.googleOAuth}/${platform}`
      );
      window.location.href = details.authorization_url;
    } catch {
      toast.error(t('configurations.integrations.connection_error'));
    }
  };

  const handleGoogleCallback = useCallback(
    (code: string, tracking_id: string, platform: string, scope: string[]) => {
      setIsFetchingSubAccounts(true);
      httpService
        .post<OAuthResponse>(API_CONFIG.path.googleOAuthCallback, {
          code,
          authorise_type: platform,
          tracking_id,
          scope,
        })
        .then(({ accounts, message, error, ...rest }) => {
          setIsFetchingSubAccounts(false);

          setIsAccountUnSaved(
            rest?.google_social_account_id
              ? String(rest.google_social_account_id)
              : undefined
          );
          if (!message && !error) {
            setAddonAccountModal((prev) => ({
              ...prev,
              email: rest.email,
              accounts,
            }));
          }
          if (accounts.length === 0) {
            if (rest?.total_accounts_count === 0) {
              toast.error(
                t(
                  "configurations.integrations.noSubAccountsTryDifferentAccount"
                )
              );
            } else {
              toast.error(
                t("configurations.integrations.allSubAccountsAttached")
              );
            }
          }
        })
        .catch((err) => {
          console.error("Error in handleGoogleCallback:", err);
          setIsFetchingSubAccounts(false);
          switch (err?.message) {
            case "MISSING_REQUIRED_GOOGLE_ADMOB_SCOPE":
            case "MISSING_REQUIRED_GOOGLE_ADS_SCOPE":
              setNoAccountWarning({
                isOpen: true,
                platform: err?.message?.includes("ADMOB")
                  ? "google_admob"
                  : "google_ads",
                email: err?.data?.email || addonAccountModal.email,
                warningType: "missing_scope",
              });
              break;
            default:
              toast.error(err?.message);
          }
        });
    },
    []
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("code");
    const scope = searchParams.get("scope");
    const addon = localStorage.getItem(LOCAL_STORAGE_KEYS.ADD_ON);
    const platform = localStorage.getItem(LOCAL_STORAGE_KEYS.PLATFORM) || "";
    const scopeArray = scope?.split(" ");

    if (code && addon && scopeArray) {
      // Handle addon account OAuth callback
      setAddonAccountModal((prev) => ({
        ...prev,
        open: true,
        trackingId: addon,
        platform: platform === "google_ads" ? "google_ads" : "google_admob",
      }));

      handleGoogleCallback(code, addon, platform, scopeArray);

      // Clear the URL parameters
      navigate("/integrations", { replace: true });
    } else if (code) {
      navigate(`/integrations${location.search}`);
    }
  }, [handleGoogleCallback, location.search, navigate]);

  // Helper function to handle individual account deletion
  const handleDeleteAccount = (
    accountId: number | string,
    direction: "inward" | "outward",
    trackingId: string
  ) => {
    setDeleteAccountModal({
      open: true,
      accountId: String(accountId),
      direction,
      trackingId,
    });
  };

  // Helper function to confirm account deletion
  const confirmDeleteAccount = async (): Promise<void> => {
    try {
      const { accountId, direction, trackingId } = deleteAccountModal;

      // Update Tracking Integration first
      if (trackingId && integrations && integrations[trackingId]) {
        const integrationSet = integrations[trackingId];
        let inwards = [...(integrationSet.inwards || [])];
        let outwards = [...(integrationSet.outwards || [])];

        if (direction === "inward") {
          inwards = inwards.filter(
            (s) => String(s.id) !== String(accountId) && String(s.social_account_id) !== String(accountId)
          );
        } else {
          outwards = outwards.filter(
            (s) => String(s.id) !== String(accountId) && String(s.social_account_id) !== String(accountId)
          );
        }

        await httpService.patch(
          `${API_CONFIG.path.trackingIntegration}/${trackingId}/update`,
          { inwards, outwards }
        );
      }

      await deleteAccountMutation.mutateAsync({
        accountId,
        isAddon: false,
      });
      toast.success(t("configurations.integrations.accountDeleted"));
      setDeleteAccountModal({ open: false, accountId: "", direction: null, trackingId: "" });
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error(t("configurations.integrations.delete_error"));
      }
    }
  };

  const handleUpdateIntegrationName = async (id: string, newName: string) => {
    try {
      await httpService.patch(
        `${API_CONFIG.path.trackingIntegration}/${id}/update`,
        { name: newName }
      );
      toast.success(t("configurations.integrations.nameUpdated", "Integration name updated"));
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    } catch (error) {
      toast.error(t("configurations.integrations.nameUpdateError", "Failed to update integration name"));
      throw error;
    }
  };

  const confirmUnSavedAccountDeletion = async (): Promise<void> => {
    try {
      if (isAccountUnSaved) {
        await deleteAccountMutation.mutateAsync({
          accountId: isAccountUnSaved,
          isAddon: true,
        });
        // toast.success(t("configurations.integrations.accountDeleted"));
      }
      setIsAccountUnSaved(null);
    } catch (error) {
      // toast.error(t("configurations.integrations.delete_error"));
      console.error("Error deleting account:", error);
      setIsAccountUnSaved(null);
    }
  };

  // Common function to handle modal close logic
  const handleModalClose = async (): Promise<void> => {
    if (isAccountUnSaved && !deleteAccountMutation.isPending) {
      await confirmUnSavedAccountDeletion();
    }
    setAddonAccountModal(initialAddonAccountModalState);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ADD_ON);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.PLATFORM);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center justify-between pl-2">
        <div>
          <div className="flex items-center gap-2">
            <GoogleLogo size={20} weight="duotone" className="text-gray-700" />
            <h1 className="text-md font-bold text-gray-700">
              {/* {t("configurations.integrations.title")} */}
              Integration Summary
            </h1>
          </div>
          <div className="text-xs text-gray-600 ">
            Manage your Spend vs Revenue pairs and track data synchronization.
          </div>
        </div>
        {!isLoading && (
          <>
            {(user?.is_owner
              ? !hasAnyIntegration
              : hasIntegrationCreatePermission) && (
                <Button
                  size="sm"
                  color="indigo"
                  onClick={() => navigate("/integrations?trackingId=add")}
                  className="w-auto"
                >
                  <div className="flex items-center justify-center gap-1">
                    <StackPlus className="h-4 w-4" />
                    {t("configurations.integrations.addNew")}
                  </div>
                </Button>
              )}
          </>
        )}
      </div>

      {isLoading ? (
        <IntegrationBlockSkeleton count={2} />
      ) : (
        <>
          <IntegrationStats
            activePairs={activeTotal}
            activeOnly={activeOnlyCount}
            ongoingPairs={ongoingCount}
            syncHealth={syncHealth}
            lastPull={lastPull || ""}
          />

          <div>
            {integrationsWithLiveStatus && Object.keys(integrationsWithLiveStatus).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-2">
                {Object.entries(integrationsWithLiveStatus)
                  .map(([key, integrationSet]) => (
                    <IntegrationCard
                      key={key}
                      trackingId={key}
                      integrationSet={integrationSet}
                      onEdit={() => {
                        localStorage.setItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE, "edit");
                        setTimeout(() => {
                          navigate(`/integrations?trackingId=${key}`);
                        }, 100);
                      }}
                      onUpdateName={(newName) => handleUpdateIntegrationName(key, newName)}
                      onDeleteAccount={handleDeleteAccount}
                      hasDeletePermission={hasAddonDeletePermission}
                    />
                  )
                  )}
                <div
                  onClick={() => navigate("/integrations?trackingId=add")}
                  className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-2xl p-6 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-all duration-300 min-h-[140px]"
                >
                  <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mb-3 group-hover:bg-indigo-100 transition-colors">
                    <StackPlus size={20} className="text-gray-400" />
                  </div>
                  <span className="text-sm font-bold text-gray-500">
                    Connect Another Pair
                  </span>
                </div>
              </div>
            ) : (
              <Card>
                <div className="p-6 text-center text-gray-500">
                  {t("configurations.integrations.noIntegrations")}
                </div>
              </Card>
            )}
          </div>

          {/* <RecentActivityLogs /> */}

          {/* Addon Account Modal */}
          <AnimatedModal
            size="lg"
            show={addonAccountModal.open}
            onClose={handleModalClose}
            title={t("configurations.integrations.subAccountSelection")}
            footer={
              <div className="flex gap-2 justify-end">
                <Button
                  color="light"
                  disabled={deleteAccountMutation.isPending}
                  onClick={handleModalClose}
                >
                  <div className="flex items-center gap-2">
                    <X className="h-4 w-4" />
                    {deleteAccountMutation.isPending
                      ? t("common.deleting")
                      : t("common.cancel")}
                  </div>
                </Button>
                <Button
                  color="indigo"
                  onClick={handleSaveAddonAccount}
                  disabled={
                    !addonAccountModal.selectedAccountId ||
                    savingAddonAccount ||
                    savingAddonAccount
                  }
                  isProcessing={savingAddonAccount}
                  processingSpinner={
                    <Spinner size="sm" color="purple" className="-mt-[2px]" />
                  }
                >
                  <div className="flex items-center gap-2">
                    {!savingAddonAccount && <FloppyDisk className="h-4 w-4" />}
                    {t("common.save")}
                  </div>
                </Button>
              </div>
            }
          >
            <div className="space-y-4">
              <div className="rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">
                  {t("configurations.integrations.accountDetails")}
                </h4>
                <p className="text-sm text-gray-600">
                  {t("configurations.integrations.selectSubAccountToAdd")}
                </p>
                <br />
                {addonAccountModal.email && (
                  <p className="text-sm text-gray-600">
                    <span className="text-gray-900 font-medium">Email: </span>
                    <span className="text-gray-600 ml-1">
                      {addonAccountModal.email}
                    </span>
                  </p>
                )}
              </div>
              {isFetchingSubAccounts || savingAddonAccount ? (
                <Loading className="flex items-center justify-center h-[100px]" />
              ) : (
                <>
                  {addonAccountModal.accounts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {convertAccountsToEntities(
                        addonAccountModal.accounts
                      ).map((account) => (
                        <div
                          key={account.id}
                          className={`p-3 border rounded-lg transition-all ${addonAccountModal.selectedAccountId ===
                            (localStorage.getItem(
                              LOCAL_STORAGE_KEYS.PLATFORM
                            ) === "google_admob"
                              ? account.publisher_id
                              : account.id)
                            ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                            : "border-gray-200"
                            }
                          ${localStorage.getItem(
                              LOCAL_STORAGE_KEYS.PLATFORM
                            ) !== "google_admob" &&
                            account.status !== ADDON_ACCOUNT_STATUS.ENABLED &&
                            "cursor-not-allowed"
                            }`}
                          onClick={() => {
                            if (
                              localStorage.getItem(
                                LOCAL_STORAGE_KEYS.PLATFORM
                              ) === "google_admob" ||
                              account.status === ADDON_ACCOUNT_STATUS.ENABLED
                            ) {
                              handleSubAccountSelection(
                                localStorage.getItem(
                                  LOCAL_STORAGE_KEYS.PLATFORM
                                ) === "google_admob"
                                  ? account.publisher_id || ""
                                  : account.id
                              );
                            }
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-sm">
                                {localStorage.getItem(
                                  LOCAL_STORAGE_KEYS.PLATFORM
                                ) === "google_admob"
                                  ? account.publisher_id
                                  : account.id}
                              </span>
                              {account.currency_code && (
                                <span className="text-xs text-gray-500 ml-2">
                                  ({account.currency_code})
                                </span>
                              )}
                              {account.status && (
                                <span
                                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium ml-2 ${account.status ===
                                    ADDON_ACCOUNT_STATUS.ENABLED
                                    ? "bg-green-100 text-green-700"
                                    : account.status ===
                                      ADDON_ACCOUNT_STATUS.SUSPENDED
                                      ? "bg-yellow-100 text-yellow-700"
                                      : account.status ===
                                        ADDON_ACCOUNT_STATUS.CANCELED ||
                                        account.status ===
                                        ADDON_ACCOUNT_STATUS.CLOSED
                                        ? "bg-red-100 text-red-700"
                                        : "bg-gray-100 text-gray-600"
                                    }`}
                                >
                                  {account.status}
                                </span>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                {account.name}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {addonAccountModal.selectedAccountId ===
                                (localStorage.getItem(
                                  LOCAL_STORAGE_KEYS.PLATFORM
                                ) === "google_admob"
                                  ? account.publisher_id
                                  : account.id) && (
                                  <div className="w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {t("configurations.integrations.noSubAccounts")}
                    </div>
                  )}
                </>
              )}
            </div>
          </AnimatedModal>

          <AnimatedModal
            show={noAccountWarning.isOpen}
            onClose={() => setNoAccountWarning(prev => ({ ...prev, isOpen: false }))}
            closeButton={false}
            title=""
          >
            <div className="text-center py-3">
              <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <MagnifyingGlass size={40} className="text-amber-500" weight="bold" />
              </div>
              <h3 className="text-2xl font-bold text-[#0f172a] mb-4">{t('configurations.integrations.findYourData')}</h3>
              <p className="text-sm text-gray-500 mb-10 leading-relaxed px-2">
                {noAccountWarning.warningType === 'no_platform_account' ? (
                  <Trans
                    i18nKey="configurations.integrations.noAccountWarningEmail"
                    values={{ email: noAccountWarning.email || 'abc@xyz.com', platform: noAccountWarning.platform === 'google_admob' ? 'AdMob account' : 'Google Ads account' }}
                    components={{ bold: <span className="font-bold text-[#0f172a]" /> }}
                  />
                ) : noAccountWarning.warningType === 'missing_scope' ? (
                  <Trans
                    i18nKey="configurations.integrations.missingScopeWarning"
                    values={{ email: noAccountWarning.email || 'abc@xyz.com', platform: noAccountWarning.platform === 'google_admob' ? 'AdMob' : 'Google Ads' }}
                    components={{ bold: <span className="font-bold text-[#0f172a]" /> }}
                  />
                ) : (
                  <Trans
                    i18nKey="configurations.integrations.noAccountWarningAvailable"
                    values={{ email: noAccountWarning.email || 'abc@xyz.com', platform: noAccountWarning.platform === 'google_admob' ? 'AdMob account' : 'Google Ads account' }}
                    components={{ bold: <span className="font-bold text-[#0f172a]" /> }}
                  />
                )}
                {" "}{t('configurations.integrations.tryAgainDesc', { direction: noAccountWarning.platform === 'google_admob' ? t('configurations.integrations.revenue') : t('configurations.integrations.spend') })}
              </p>
              <div className="flex gap-4 w-full">
                <Button
                  color="light"
                  className='w-full'
                  onClick={() => setNoAccountWarning(prev => ({ ...prev, isOpen: false }))}
                >
                  {t('common.close')}
                </Button>
                <Button
                  color="indigo"
                  className='w-full'
                  onClick={() => {
                    setNoAccountWarning(prev => ({ ...prev, isOpen: false }));
                    handleConnect(noAccountWarning.platform);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <ArrowsClockwise size={18} weight="bold" />
                    {noAccountWarning.warningType === 'missing_scope'
                      ? t('configurations.integrations.connectAnotherAccount')
                      : t('configurations.integrations.tryAnotherEmail')}
                  </div>
                </Button>

              </div>
            </div>
          </AnimatedModal>

          {/* Google IAP Modal */}
          <AnimatedModal
            size="md"
            show={googleIAPModal.open}
            onClose={() => {
              if (googleIapSubmitting || insertGoogleIAPMutation.isPending)
                return;
              setGoogleIAPModal({ open: false, trackingId: "" });
              resetGoogleIapForm();
            }}
            title={t("configurations.integrations.googleIap.title")}
            footer={
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  color="light"
                  type="button"
                  onClick={() => {
                    if (insertGoogleIAPMutation.isPending) return;
                    setGoogleIAPModal({ open: false, trackingId: "" });
                    resetGoogleIapForm();
                  }}
                >
                  <div className="flex items-center gap-2">
                    <X className="h-4 w-4" />
                    {t("common.cancel")}
                  </div>
                </Button>
                <Button
                  color="indigo"
                  type="button"
                  onClick={handleSubmitGoogleIAP(onSubmitGoogleIAP)}
                  isProcessing={
                    googleIapSubmitting || insertGoogleIAPMutation.isPending
                  }
                  processingSpinner={<Spinner size="sm" color="purple" />}
                >
                  <div className="flex items-center gap-2">
                    {!(
                      googleIapSubmitting || insertGoogleIAPMutation.isPending
                    ) && <FloppyDisk className="h-4 w-4" />}
                    {t("common.submit")}
                  </div>
                </Button>
              </div>
            }
          >
            <div className="space-y-4">
              <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-xs text-indigo-800">
                <span>Need help setting this up? </span>
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={() => {
                    if (insertGoogleIAPMutation.isPending) return;
                    resetGoogleIapForm();
                    window.open("/support#google", "_blank");
                  }}
                >
                  Open Google IAP guide
                </button>
              </div>
              <form
                className="grid grid-cols-1 gap-3"
                onSubmit={handleSubmitGoogleIAP(onSubmitGoogleIAP)}
                noValidate
              >
                <div className="space-y-6">
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor="service_account_json"
                      value="Service Account Key File (JSON)"
                    />
                    <p className="text-xs text-gray-500 mb-1">
                      {t(
                        "configurations.integrations.googleIap.serviceAccountKeyDescription"
                      )}
                    </p>
                    <FileInput
                      id="service_account_json"
                      accept="application/json,.json"
                      {...registerGoogleIAP("service_account_json")}
                    />
                    {googleIapErrors?.service_account_json && (
                      <span className="text-xs text-red-600">
                        {googleIapErrors.service_account_json.message as string}
                      </span>
                    )}
                    {/* {(() => {
                    const files = watchGoogleIAP("service_account_json") as FileList | undefined;
                    if (files && files.length > 0) {
                      return <Badge color="info" className="w-fit mt-1">{files[0].name}</Badge>;
                    }
                    return null;
                  })()} */}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor="email"
                      value="Service Account Email Address"
                    />
                    <p className="text-xs text-gray-500 mb-1">
                      <a
                        href="https://console.cloud.google.com/iam-admin/serviceaccounts"
                        target="_blank"
                        className="text-blue-600 underline"
                      >{`IAM & Admin > Service accounts`}</a>
                    </p>
                    <TextInput
                      id="email"
                      type="email"
                      placeholder={
                        "e.g. apprevbooster@project-id.iam.gserviceaccount.com"
                      }
                      {...registerGoogleIAP("email")}
                    />
                    {googleIapErrors?.email && (
                      <span className="text-xs text-red-600">
                        {googleIapErrors.email.message as string}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor="bucket_name"
                      value="Financial Report Bucket Name"
                    />
                    <p className="text-xs text-gray-500 mb-1">
                      <a
                        href="https://play.google.com/console/u/1/developers"
                        target="_blank"
                        className="text-blue-600 underline"
                      >{`Download Reports > Financial > Earning Reports > "Copy Cloud Storage URI"`}</a>
                    </p>
                    <TextInput
                      id="bucket_name"
                      placeholder={
                        "eg., gs://pubsite_prod_rev_05493956521912004335/earnings/"
                      }
                      {...registerGoogleIAP("bucket_name")}
                    />
                    {googleIapErrors?.bucket_name && (
                      <span className="text-xs text-red-600">
                        {googleIapErrors.bucket_name.message as string}
                      </span>
                    )}
                  </div>
                </div>
              </form>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                {t("configurations.integrations.googleIapNote")}
              </div>
            </div>
          </AnimatedModal>

          {/* Apple IAP Modal */}
          <AnimatedModal
            size="md"
            show={appleIAPModal.open}
            onClose={() => {
              // Silent close, delete any unsaved addon account if exists
              handleModalClose();
              resetAppleForm();
              setAppleIAPModal({ open: false, trackingId: "" });
            }}
            title={t("configurations.integrations.appleIap.title")}
            footer={
              <div className="flex gap-2 justify-end">
                <Button
                  color="light"
                  onClick={() => {
                    handleModalClose();
                    resetAppleForm();
                    setAppleIAPModal({ open: false, trackingId: "" });
                  }}
                  disabled={appleSubmitting || insertAppleIAPMutation.isPending}
                >
                  <div className="flex items-center gap-2">
                    <X className="h-4 w-4" />
                    {t("common.cancel")}
                  </div>
                </Button>
                <Button
                  color="indigo"
                  onClick={handleSubmitApple(onSubmitAppleIAP)}
                  disabled={appleSubmitting || insertAppleIAPMutation.isPending}
                  isProcessing={
                    appleSubmitting || insertAppleIAPMutation.isPending
                  }
                  processingSpinner={
                    <Spinner size="sm" color="purple" className="-mt-[2px]" />
                  }
                >
                  <div className="flex items-center gap-2">
                    {!(appleSubmitting || insertAppleIAPMutation.isPending) && (
                      <FloppyDisk className="h-4 w-4" />
                    )}
                    {t("common.save")}
                  </div>
                </Button>
              </div>
            }
          >
            <div className="space-y-4">
              <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-xs text-indigo-800">
                <span>Need help setting this up? </span>
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={() => {
                    if (insertAppleIAPMutation.isPending) return;
                    resetAppleForm();
                    window.open("/support#apple", "_blank");
                  }}
                >
                  Open Apple IAP guide
                </button>
              </div>
              <form
                className="space-y-4"
                onSubmit={handleSubmitApple(onSubmitAppleIAP)}
              >
                <div>
                  <Label htmlFor="p8_path" value="API Key File (.p8)" />
                  <p className="text-xs text-gray-500 my-1">
                    {t("configurations.integrations.appleIap.note")}
                  </p>
                  <FileInput
                    id="p8_path"
                    accept=".p8,.pem"
                    {...registerApple("p8_path")}
                  />
                  {appleErrors.p8_path && (
                    <p className="text-red-600 text-xs mt-1">
                      {appleErrors.p8_path.message as string}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="key_id" value="Key ID" />
                  <p className="text-xs text-gray-500 mb-1">
                    <a
                      href="https://appstoreconnect.apple.com/access/integrations/api"
                      target="_blank"
                      className="text-blue-600 underline"
                    >
                      {`Users and Access > Keys > Integration`}
                    </a>
                  </p>
                  <TextInput
                    id="key_id"
                    placeholder="e.g., 2SD45F567"
                    {...registerApple("key_id")}
                  />
                  {appleErrors.key_id && (
                    <p className="text-red-600 text-xs mt-1">
                      {appleErrors.key_id.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="issuer_id" value="Issuer ID" />
                  <p className="text-xs text-gray-500 mb-1">
                    <a
                      href="https://appstoreconnect.apple.com/access/integrations/api"
                      target="_blank"
                      className="text-blue-600 underline"
                    >
                      {`Users and Access > Keys`}
                    </a>
                  </p>
                  <TextInput
                    id="issuer_id"
                    placeholder="e.g., 9TG45G5H77TH4DF"
                    {...registerApple("issuer_id")}
                  />
                  {appleErrors.issuer_id && (
                    <p className="text-red-600 text-xs mt-1">
                      {appleErrors.issuer_id.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="vendor_number" value="Apple Vendor ID" />
                  <p className="text-xs text-gray-500 mb-1">
                    <a
                      href="https://appstoreconnect.apple.com/trends"
                      target="_blank"
                      className="text-blue-600 underline"
                    >
                      {`App Store Connect > Payments and Financial Reports >  Vendor #12345678`}
                    </a>
                  </p>
                  <TextInput
                    id="vendor_number"
                    placeholder="e.g., 1234567890"
                    {...registerApple("vendor_number")}
                  />
                  {appleErrors.vendor_number && (
                    <p className="text-red-600 text-xs mt-1">
                      {appleErrors.vendor_number.message}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </AnimatedModal>

          <AnimatedModal
            size="md"
            show={editModal.open}
            onClose={() => setEditModal(initialModalState)}
            title={t("configurations.integrations.editEmail")}
            footer={
              <div className="flex gap-2 justify-end">
                <Button
                  color="light"
                  onClick={() => setEditModal(initialModalState)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  color="indigo"
                  onClick={() => {
                    setEditModal(initialModalState);
                  }}
                >
                  {t("common.save")}
                </Button>
              </div>
            }
          >
            <TextInput
              value={editModal.email}
              onChange={(e) =>
                setEditModal({ ...editModal, email: e.target.value })
              }
            />
          </AnimatedModal>

          <AnimatedModal
            size="md"
            show={confirmModal.open}
            onClose={() => setConfirmModal(initialConfirmationPopUpState)}
            title={t("common.confirmation")}
            footer={
              <div className="flex gap-2 justify-end">
                <Button
                  color="light"
                  onClick={() => setConfirmModal(initialConfirmationPopUpState)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  color={
                    confirmModal.type === "connect" ? "success" : "failure"
                  }
                  onClick={() => {
                    setConfirmModal(initialConfirmationPopUpState);
                  }}
                >
                  {t("common.confirm")}
                </Button>
              </div>
            }
          >
            <p>
              {confirmModal.type === "connect"
                ? t("configurations.integrations.confirmConnect")
                : t("configurations.integrations.confirmDisconnect")}
            </p>
          </AnimatedModal>

          <AnimatedModal
            isConfirmation
            show={deleteAccountModal.open}
            onClose={() =>
              setDeleteAccountModal({
                open: false,
                accountId: "",
                direction: null,
                trackingId: "",
              })
            }
            onConfirm={confirmDeleteAccount}
            isConfirming={deleteAccountMutation.isPending}
            title={t("common.confirmation")}
            children={
              <div className="space-y-3">
                <p className="text-gray-700 text-center">
                  {t("configurations.integrations.confirmDeleteAccount")}
                </p>
                {/* <p className="text-xs text-gray-700 text-center">
                  {t("configurations.integrations.deleteConsentNote")}
                </p> */}
              </div>
            }
            confirmText={
              deleteAccountMutation.isPending
                ? t("common.deleting")
                : t("common.delete")
            }
            confirmButtonColor="red"
            cancelText={t("common.cancel")}
          />

          {/* App Selection Modal — shown after Apple/Google IAP connect for non-VIP users */}
          {appSelectionData && (
            <AppSelectionModal
              isOpen={true}
              platform={appSelectionData.platform}
              apps={appSelectionData.apps}
              planLimit={appSelectionData.planLimit}
              socialAccountId={appSelectionData.socialAccountId}
              onClose={() => setAppSelectionData(null)}
              onSuccess={() => {
                setAppSelectionData(null);
                markIntegrationComplete();
              }}
            />
          )}
        </>
      )
      }
    </div >
  );
};

export default Integrations;
